/**
 * Admin Product Management — Production Supabase Database Integration.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  ShieldAlert,
  Package,
  AlertTriangle,
  CheckCircle2,
  X,
  ExternalLink,
} from "lucide-react";
import { useCurrentUser } from "@/hooks/use-auth";
import { SearchInput } from "@/components/forms/search-input";
import { formatPrice } from "@/components/forms/price-display";
import { ProductFormDialog } from "@/components/admin/products/product-form-dialog";
import {
  fetchAdminProductsFromSupabase,
  reconcileOrphanedProducts,
  syncProductToSupabase,
  deleteProductFromSupabase,
  type ExtendedProduct,
} from "@/features/admin/products/store/admin-products-store";
import {
  fetchAdminCategoriesFromSupabase,
} from "@/features/admin/categories/store/admin-categories-store";
import { siteConfig } from "@/config/site";
import { LoadingSkeleton } from "@/components/common/loading-skeleton";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";

export const Route = createFileRoute("/admin/products")({
  head: () => ({
    meta: [
      { title: `Product Management — Admin — ${siteConfig.name}` },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminProducts,
});

function AdminProducts() {
  const { isAuthenticated, isAdmin, isLoading, user } = useCurrentUser();
  const [query, setQuery] = useState("");
  const [productList, setProductList] = useState<ExtendedProduct[]>([]);
  const [fetchingDb, setFetchingDb] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Edit / Create Dialog State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ExtendedProduct | null>(null);

  // Custom Delete Modal State
  const [deletingProduct, setDeletingProduct] = useState<ExtendedProduct | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useLockBodyScroll(!!deletingProduct);

  const loadProducts = async () => {
    setFetchingDb(true);
    try {
      const [dbProducts, dbCategories] = await Promise.all([
        fetchAdminProductsFromSupabase(),
        fetchAdminCategoriesFromSupabase(),
      ]);
      const validNames = dbCategories.map((c) => c.name);
      const cleanProducts = await reconcileOrphanedProducts(dbProducts, validNames);
      setProductList(cleanProducts);
    } catch (e) {
      console.error("Failed to load products from Supabase", e);
      setProductList([]);
    } finally {
      setFetchingDb(false);
    }
  };

  const handleCreateNew = () => {
    setEditingProduct(null);
    setIsFormOpen(true);
  };

  const handleEdit = (product: ExtendedProduct) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleSaveProduct = async (productData: Partial<ExtendedProduct>) => {
    const prevProducts = [...productList];
    const existingIdx = productList.findIndex((p) => p.id === productData.id);
    let updated: ExtendedProduct[];
    if (existingIdx >= 0) {
      updated = [...productList];
      updated[existingIdx] = { ...updated[existingIdx], ...productData } as ExtendedProduct;
    } else {
      updated = [productData as ExtendedProduct, ...productList];
    }
    setProductList(updated);

    try {
      await syncProductToSupabase(productData);
      setIsFormOpen(false);
      setEditingProduct(null);
    } catch (err: unknown) {
      console.error("Save product to Supabase failed:", err);
      setProductList(prevProducts);
      alert(
        `Failed to save product in database: ${(err as { message?: string }).message || "Unknown error"}`,
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingProduct) return;
    setIsDeleting(true);
    const prevProducts = [...productList];
    try {
      await deleteProductFromSupabase(deletingProduct.id);
      const updated = productList.filter((p) => p.id !== deletingProduct.id);
      setProductList(updated);
    } catch (err: unknown) {
      console.error("Delete product failed:", err);
      setProductList(prevProducts);
      alert(
        `Failed to delete product from database: ${(err as { message?: string }).message || "Unknown error"}`,
      );
    } finally {
      setIsDeleting(false);
      setDeletingProduct(null);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  if (isLoading) {
    return (
      <div className="section-container py-32 text-center">
        <LoadingSkeleton className="h-64 w-full max-w-3xl mx-auto" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="section-container max-w-xl py-32 text-center">
        <ShieldAlert className="mx-auto h-12 w-12 text-amber-500" />
        <h1 className="mt-4 font-display text-2xl">Authentication Required</h1>
        <p className="mt-2 text-xs text-muted-foreground">
          Please sign in to access product management.
        </p>
        <Link
          to="/account/login"
          className="mt-6 inline-flex min-h-11 items-center bg-primary px-6 text-xs uppercase tracking-wider text-primary-foreground hover:opacity-90"
        >
          Sign In
        </Link>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="section-container max-w-xl py-32 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <ShieldAlert className="h-8 w-8" />
        </div>
        <p className="eyebrow mt-6 text-destructive">403 Forbidden</p>
        <h1 className="mt-2 font-display text-3xl">Access Denied</h1>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          Your account (<strong className="text-foreground">{user?.email}</strong>) does not have
          administrator privileges.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Link
            to="/account"
            className="inline-flex min-h-11 items-center border border-border px-6 text-xs uppercase tracking-wider hover:bg-secondary"
          >
            Return to My Account
          </Link>
        </div>
      </div>
    );
  }

  const filtered = productList.filter(
    (p) =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.category.toLowerCase().includes(query.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginatedProducts = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b pb-6">
        <div>
          <p className="eyebrow">Inventory Console</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl">Product Management</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCreateNew}
            className="inline-flex min-h-11 items-center gap-2 bg-primary px-6 text-sm font-semibold tracking-[0.16em] uppercase text-primary-foreground hover:opacity-90 transition-opacity rounded-lg shadow-xs"
          >
            <Plus className="h-4 w-4" /> Add New Product
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-between items-center">
        <SearchInput
          value={query}
          onChange={(v) => {
            setQuery(v);
            setCurrentPage(1);
          }}
          placeholder="Filter products by name or category..."
          className="max-w-md text-sm"
        />
        <div className="text-sm font-medium text-muted-foreground">
          Showing <span className="font-bold text-foreground">{filtered.length}</span> items
        </div>
      </div>

      <div className="bg-card border border-border/90 rounded-2xl shadow-xs overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground">
            <Package className="mx-auto h-12 w-12 text-muted-foreground/50" strokeWidth={1} />
            <p className="mt-3 text-base font-semibold">No products found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Click 'Add New Product' to create a new product entry.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-border bg-secondary/30 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="py-4 px-6">Product</th>
                  <th className="py-4 px-6">Category</th>
                  <th className="py-4 px-6">Price</th>
                  <th className="py-4 px-6">Stock Status</th>
                  <th className="py-4 px-6">Rating</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60 text-sm font-medium">
                {paginatedProducts.map((product) => (
                  <tr 
                    key={product.id} 
                    className="hover:bg-secondary/20 transition-colors cursor-pointer"
                    onClick={() => handleEdit(product)}
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3.5">
                        <img
                          src={product.image}
                          alt={product.name}
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src = "/plantpicture/p1/5.webp";
                          }}
                          className="h-12 w-12 object-cover rounded-xl border bg-secondary shrink-0 shadow-2xs"
                        />
                        <div>
                          <p className="font-bold text-base text-foreground">{product.name}</p>
                          <p className="text-xs text-muted-foreground">{product.note}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm font-medium">
                      {product.category?.toLowerCase() === "uncategorized" || !product.category ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-200 dark:border-amber-900">
                          <AlertTriangle className="h-3 w-3 text-amber-600" /> Uncategorized
                        </span>
                      ) : (
                        <span className="text-foreground/90 font-medium">{product.category}</span>
                      )}
                    </td>
                    <td className="py-4 px-6 font-display text-base font-semibold tabular-nums">
                      {formatPrice(product.price)}
                    </td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 dark:bg-emerald-950 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 className="h-3.5 w-3.5" /> In Stock ({product.stockQuantity})
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm font-medium tabular-nums">
                      ★ {product.rating} ({product.reviewsCount})
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          href={`/product/${product.slug || product.id}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 text-muted-foreground hover:text-emerald-700 hover:bg-secondary rounded-lg transition-colors"
                          title="View on Storefront"
                        >
                          <ExternalLink className="h-4.5 w-4.5" />
                        </a>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(product);
                          }}
                          className="p-2 text-muted-foreground hover:text-primary hover:bg-secondary rounded-lg transition-colors"
                          title="Edit Product"
                        >
                          <Edit className="h-4.5 w-4.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingProduct(product);
                          }}
                          className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                          title="Delete Product"
                        >
                          <Trash2 className="h-4.5 w-4.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filtered.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-medium text-foreground">{Math.min(currentPage * pageSize, filtered.length)}</span> of <span className="font-medium text-foreground">{filtered.length}</span> results
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded border border-border text-xs font-semibold hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-muted-foreground font-medium px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded border border-border text-xs font-semibold hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit / Create Product Modal */}
      <ProductFormDialog
        open={isFormOpen}
        product={editingProduct}
        onClose={() => {
          setIsFormOpen(false);
          setEditingProduct(null);
        }}
        onSave={handleSaveProduct}
      />

      {/* Custom Modern Delete Confirmation Modal */}
      {deletingProduct && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md p-6 space-y-5 animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b pb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg text-foreground">Delete Product</h3>
                  <p className="text-xs text-muted-foreground">Action cannot be undone</p>
                </div>
              </div>
              <button
                onClick={() => setDeletingProduct(null)}
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">
              Are you sure you want to delete{" "}
              <strong className="text-foreground">{deletingProduct.name}</strong>? This item will be
              permanently removed from your catalog and database.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setDeletingProduct(null)}
                disabled={isDeleting}
                className="px-4 py-2.5 border border-border rounded text-xs font-semibold uppercase tracking-wider text-foreground hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-5 py-2.5 bg-destructive text-destructive-foreground rounded text-xs font-semibold uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? "Deleting..." : "Delete Product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
